import {
  useCreateMqttDevice,
  useUpdateMqttDevice,
  getGetAllMqttDevicesQueryKey,
  type MqttCreateDeviceRequestDeviceType,
  type MqttBroker,
  type MqttBufferedMessage,
  type MqttDevice,
} from '@smarthome/api';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { MqttDeviceForm, type DeviceFormData } from './MqttDeviceForm';

import { Button, SlideOutPanel } from '@/components/ui';

export interface MqttDevicePanelProps {
  /** Whether the panel is open */
  isOpen: boolean;
  /** Called when panel should close */
  onClose: () => void;
  /** Pre-selected broker ID (when creating from monitor page) */
  brokerId?: string;
  /** Sample message for context (when creating from monitor page) */
  sampleMessage?: MqttBufferedMessage | null;
  /** Existing device to edit */
  device?: MqttDevice | null;
  /** Source device for duplication (will pre-fill form but create new) */
  duplicateFrom?: MqttDevice | null;
  /** Optional list of brokers to pass to the form */
  brokers?: MqttBroker[];
  /** Called after successful creation/update */
  onSuccess?: () => void;
}

export function MqttDevicePanel({
  isOpen,
  onClose,
  brokerId,
  sampleMessage,
  device,
  duplicateFrom,
  brokers,
  onSuccess,
}: MqttDevicePanelProps) {
  const queryClient = useQueryClient();
  const createMutation = useCreateMqttDevice();
  const updateMutation = useUpdateMqttDevice();
  const [error, setError] = useState<string | null>(null);

  const isEditing = !!device;
  const isDuplicating = !!duplicateFrom;

  // Determine title and subtitle
  const title = isEditing
    ? 'Edit Device'
    : isDuplicating
      ? 'Duplicate Device'
      : sampleMessage
        ? 'Create Device from Message'
        : 'Add Device';

  const subtitle = isEditing
    ? 'Update device configuration'
    : isDuplicating
      ? 'Create a new device based on an existing one'
      : sampleMessage
        ? 'Map an MQTT topic to a controllable device'
        : 'Configure a new MQTT device';

  // Build initial values
  const getInitialValues = useCallback((): Partial<DeviceFormData> => {
    if (device) {
      // Editing existing device
      const initialValues: Partial<DeviceFormData> = {
        id: device.id,
        brokerId: device.brokerId,
        name: device.name,
        deviceType: device.deviceType as DeviceFormData['deviceType'],
        commandTopic: device.commandTopic,
        stateTopic: device.stateTopic ?? '',
        propertyMappings: device.propertyMappings?.map((m) => ({
          propertyName: m.propertyName ?? '',
          commandEnabled: m.commandEnabled ?? true,
          commandMode: (m.commandMode ?? 'simple') as 'simple' | 'advanced',
          commandTopicOverride: m.commandTopicOverride ?? '',
          commandJsonPath: m.commandJsonPath ?? '',
          commandValueMap: m.commandValueMap ?? {},
          commandTemplate: m.commandTemplate ?? '',
          stateEnabled: m.stateEnabled ?? true,
          stateMode: (m.stateMode ?? 'simple') as 'simple' | 'advanced',
          stateTopicOverride: m.stateTopicOverride ?? '',
          stateJsonPath: m.stateJsonPath ?? '',
          stateValueMap: m.stateValueMap ?? {},
          stateQuery: m.stateQuery ?? '',
        })),
        commandMappings: device.commandMappings?.map((m) => ({
          command: m.command ?? '',
          commandEnabled: m.commandEnabled ?? true,
          commandMode: (m.commandMode ?? 'simple') as 'simple' | 'advanced',
          commandTopicOverride: m.commandTopicOverride ?? '',
          commandJsonPath: m.commandJsonPath ?? '',
          commandValueMap: m.commandValueMap ?? {},
          commandTemplate: m.commandTemplate ?? '',
        })),
      };
      if (device.switchPositions?.positions?.length) {
        initialValues.switchPositions = {
          positions: device.switchPositions.positions.map((p) => ({
            id: p.id ?? '',
            label: p.label ?? '',
            commandPayload: p.commandPayload ?? '',
            stateValue: p.stateValue ?? '',
          })),
          stateJsonPath: device.switchPositions.stateJsonPath ?? '',
        };
      }
      return initialValues;
    }
    if (duplicateFrom) {
      // Duplicating - same values but clear ID
      const initialValues: Partial<DeviceFormData> = {
        id: '', // User must provide new ID
        brokerId: duplicateFrom.brokerId,
        name: `${duplicateFrom.name} (copy)`,
        deviceType: duplicateFrom.deviceType as DeviceFormData['deviceType'],
        commandTopic: duplicateFrom.commandTopic,
        stateTopic: duplicateFrom.stateTopic ?? '',
        propertyMappings: duplicateFrom.propertyMappings?.map((m) => ({
          propertyName: m.propertyName ?? '',
          commandEnabled: m.commandEnabled ?? true,
          commandMode: (m.commandMode ?? 'simple') as 'simple' | 'advanced',
          commandTopicOverride: m.commandTopicOverride ?? '',
          commandJsonPath: m.commandJsonPath ?? '',
          commandValueMap: m.commandValueMap ?? {},
          commandTemplate: m.commandTemplate ?? '',
          stateEnabled: m.stateEnabled ?? true,
          stateMode: (m.stateMode ?? 'simple') as 'simple' | 'advanced',
          stateTopicOverride: m.stateTopicOverride ?? '',
          stateJsonPath: m.stateJsonPath ?? '',
          stateValueMap: m.stateValueMap ?? {},
          stateQuery: m.stateQuery ?? '',
        })),
        commandMappings: duplicateFrom.commandMappings?.map((m) => ({
          command: m.command ?? '',
          commandEnabled: m.commandEnabled ?? true,
          commandMode: (m.commandMode ?? 'simple') as 'simple' | 'advanced',
          commandTopicOverride: m.commandTopicOverride ?? '',
          commandJsonPath: m.commandJsonPath ?? '',
          commandValueMap: m.commandValueMap ?? {},
          commandTemplate: m.commandTemplate ?? '',
        })),
      };
      if (duplicateFrom.switchPositions?.positions?.length) {
        initialValues.switchPositions = {
          positions: duplicateFrom.switchPositions.positions.map((p) => ({
            id: p.id ?? '',
            label: p.label ?? '',
            commandPayload: p.commandPayload ?? '',
            stateValue: p.stateValue ?? '',
          })),
          stateJsonPath: duplicateFrom.switchPositions.stateJsonPath ?? '',
        };
      }
      return initialValues;
    }
    if (sampleMessage) {
      // Creating from message
      const defaultId = sampleMessage.topic
        ?.replace(/\//g, '-')
        .replace(/[^a-z0-9-]/gi, '')
        .toLowerCase();
      return {
        id: defaultId ?? '',
        brokerId: brokerId,
        name: sampleMessage.topic?.split('/').pop() ?? '',
        commandTopic: sampleMessage.topic ?? '',
      };
    }
    // Creating new
    return {
      brokerId: brokerId,
    };
  }, [device, duplicateFrom, sampleMessage, brokerId]);

  const handleSubmit = async (data: DeviceFormData) => {
    setError(null);
    try {
      if (isEditing && device?.id) {
        // Update existing device
        const hasPositions =
          data.switchPositions?.positions && data.switchPositions.positions.length >= 2;
        await updateMutation.mutateAsync({
          id: device.id,
          data: {
            brokerId: data.brokerId,
            name: data.name,
            deviceType: data.deviceType as MqttCreateDeviceRequestDeviceType,
            commandTopic: data.commandTopic,
            stateTopic: data.stateTopic || undefined,
            switchPositions: hasPositions ? data.switchPositions : undefined,
            propertyMappings: hasPositions
              ? []
              : data.propertyMappings.map((pm) => ({
                  propertyName: pm.propertyName,
                  commandEnabled: pm.commandEnabled,
                  commandMode: pm.commandMode,
                  commandTopicOverride: pm.commandTopicOverride || undefined,
                  commandJsonPath: pm.commandJsonPath || undefined,
                  commandValueMap: pm.commandValueMap,
                  commandTemplate: pm.commandTemplate || undefined,
                  stateEnabled: pm.stateEnabled,
                  stateMode: pm.stateMode,
                  stateTopicOverride: pm.stateTopicOverride || undefined,
                  stateJsonPath: pm.stateJsonPath || undefined,
                  stateValueMap: pm.stateValueMap,
                  stateQuery: pm.stateQuery || undefined,
                })),
            commandMappings: data.commandMappings?.map((cm) => ({
              command: cm.command,
              commandEnabled: cm.commandEnabled,
              commandMode: cm.commandMode,
              commandTopicOverride: cm.commandTopicOverride || undefined,
              commandJsonPath: cm.commandJsonPath || undefined,
              commandValueMap: cm.commandValueMap,
              commandTemplate: cm.commandTemplate || undefined,
            })),
          },
        });
      } else {
        // Create new device
        const createHasPositions =
          data.switchPositions?.positions && data.switchPositions.positions.length >= 2;
        await createMutation.mutateAsync({
          data: {
            id: data.id,
            brokerId: data.brokerId,
            name: data.name,
            deviceType: data.deviceType as MqttCreateDeviceRequestDeviceType,
            commandTopic: data.commandTopic,
            stateTopic: data.stateTopic || undefined,
            switchPositions: createHasPositions ? data.switchPositions : undefined,
            propertyMappings: createHasPositions
              ? []
              : data.propertyMappings.map((pm) => ({
                  propertyName: pm.propertyName,
                  commandEnabled: pm.commandEnabled,
                  commandMode: pm.commandMode,
                  commandTopicOverride: pm.commandTopicOverride || undefined,
                  commandJsonPath: pm.commandJsonPath || undefined,
                  commandValueMap: pm.commandValueMap,
                  commandTemplate: pm.commandTemplate || undefined,
                  stateEnabled: pm.stateEnabled,
                  stateMode: pm.stateMode,
                  stateTopicOverride: pm.stateTopicOverride || undefined,
                  stateJsonPath: pm.stateJsonPath || undefined,
                  stateValueMap: pm.stateValueMap,
                  stateQuery: pm.stateQuery || undefined,
                })),
            commandMappings: data.commandMappings?.map((cm) => ({
              command: cm.command,
              commandEnabled: cm.commandEnabled,
              commandMode: cm.commandMode,
              commandTopicOverride: cm.commandTopicOverride || undefined,
              commandJsonPath: cm.commandJsonPath || undefined,
              commandValueMap: cm.commandValueMap,
              commandTemplate: cm.commandTemplate || undefined,
            })),
          },
        });
      }

      // Invalidate queries and close
      queryClient.invalidateQueries({ queryKey: getGetAllMqttDevicesQueryKey() });
      onSuccess?.();
      onClose();
    } catch (err) {
      const apiError = err as { message?: string; code?: string };
      if (apiError.code === 'CONFLICT' || apiError.message?.includes('already exists')) {
        setError('A device with this ID already exists.');
      } else {
        setError(isEditing ? 'Failed to update device.' : 'Failed to create device.');
      }
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  const footer = (
    <>
      <Button type="button" variant="ghost" onClick={onClose} disabled={isLoading}>
        Cancel
      </Button>
      <Button type="submit" form="device-form" loading={isLoading}>
        {isEditing ? 'Save Changes' : isDuplicating ? 'Create Copy' : 'Create Device'}
      </Button>
    </>
  );

  return (
    <SlideOutPanel
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      size="lg"
      footer={footer}
    >
      <MqttDeviceForm
        formId="device-form"
        initialValues={getInitialValues()}
        preselectedBrokerId={brokerId}
        sampleMessage={sampleMessage}
        onSubmit={handleSubmit}
        isLoading={isLoading}
        error={error}
        isEditing={isEditing}
        brokers={brokers}
      />
    </SlideOutPanel>
  );
}
